import {
  type ClipboardEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { cn } from "@/shared/lib/utils";

interface OtpInputProps {
  /** Valor controlado (0..length). RHF-friendly. */
  value: string;
  /** Setter — emite el string completo normalizado (solo dígitos, max length). */
  onChange: (next: string) => void;
  /** Opcional: callback cuando el usuario llena los 6 slots. Usado para auto-submit. */
  onComplete?: (code: string) => void;
  /** Cantidad de slots. Default 6 (TOTP standard). */
  length?: number;
  /** Error visual — dispara shake + border destructive. */
  invalid?: boolean;
  /** Deshabilita toda la entrada (p.ej. durante verify loading). */
  disabled?: boolean;
  /** Auto-focus al primer slot al montar. */
  autoFocus?: boolean;
  /** Label accesible agregado al group. */
  "aria-label"?: string;
  /** Describe-by para asociar errores externos. */
  "aria-describedby"?: string;
  /** Clase extra para el group. */
  className?: string;
}

export interface OtpInputHandle {
  /** Hace focus al primer slot. */
  focus: () => void;
  /** Limpia el value y pone focus al primer slot. */
  clear: () => void;
}

const DIGIT_RE = /^\d$/;

/**
 * OTP input blindado para flujos TOTP / email verification.
 *
 * Decisiones:
 *  - **Un slot = un `<input maxLength={1}>` real** (no un div con render
 *    custom). Esto da autofill del navegador, soporte iOS "autofill from
 *    SMS", y accesibilidad nativa gratis.
 *  - **Caret policy**: siempre colocamos el caret al final del slot al
 *    focus-in para evitar inserts en medio del slot.
 *  - **Backspace empty → prev**: comportamiento esperado; backspace con
 *    dígito solo borra.
 *  - **Paste**: si viene un string con N dígitos, los distribuye; si
 *    excede, trunca al length.
 *  - **Auto-complete**: `one-time-code` lo activa en todos los slots;
 *    el navegador puebla el primero y el componente dispatcha el resto.
 *  - **Shake**: aplicamos solo cuando `invalid` cambió de false→true, no
 *    cada render. Gated por reduced motion vía CSS global.
 */
export const OtpInput = forwardRef<OtpInputHandle, OtpInputProps>(function OtpInput(
  {
    value,
    onChange,
    onComplete,
    length = 6,
    invalid = false,
    disabled = false,
    autoFocus = false,
    className,
    "aria-label": ariaLabel = "Código de verificación",
    "aria-describedby": ariaDescribedBy,
  },
  ref,
) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const prevInvalidRef = useRef(invalid);
  const groupRef = useRef<HTMLDivElement | null>(null);

  // Normaliza el value a un array de length chars (padded con "").
  const chars = useMemo(() => {
    const sanitized = value.replace(/\D/g, "").slice(0, length);
    return Array.from({ length }, (_, i) => sanitized[i] ?? "");
  }, [value, length]);

  const focusAt = useCallback((index: number) => {
    const clamped = Math.max(0, Math.min(index, inputsRef.current.length - 1));
    const el = inputsRef.current[clamped];
    el?.focus();
    el?.select();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => focusAt(0),
      clear: () => {
        onChange("");
        focusAt(0);
      },
    }),
    [focusAt, onChange],
  );

  useEffect(() => {
    if (autoFocus) focusAt(0);
    // Intencional: solo al mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Dispara shake cuando pasamos a invalid. Lo hacemos manualmente
  // removiendo y re-aplicando la clase para que la animación corra cada
  // vez que entre un error nuevo (no solo la primera vez).
  useEffect(() => {
    if (invalid && !prevInvalidRef.current) {
      const el = groupRef.current;
      if (el) {
        el.classList.remove("animate-shake");
        // Force reflow para reiniciar la animación.
        void el.offsetWidth;
        el.classList.add("animate-shake");
      }
    }
    prevInvalidRef.current = invalid;
  }, [invalid]);

  const commit = useCallback(
    (nextChars: string[]) => {
      const joined = nextChars.join("");
      onChange(joined);
      if (joined.length === length && !joined.includes("") && onComplete) {
        onComplete(joined);
      }
    },
    [onChange, onComplete, length],
  );

  const handleChange = useCallback(
    (index: number, raw: string) => {
      const digit = raw.replace(/\D/g, "").slice(-1); // último dígito tipeado
      if (!digit) {
        // Borrado "soft" dentro del slot.
        const next = [...chars];
        next[index] = "";
        commit(next);
        return;
      }
      const next = [...chars];
      next[index] = digit;
      commit(next);
      focusAt(index + 1);
    },
    [chars, commit, focusAt],
  );

  const handleKeyDown = useCallback(
    (index: number, e: ReactKeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      if (key === "Backspace") {
        if (chars[index]) {
          // Deja que el input borre el dígito.
          return;
        }
        // Vacío → retroceder y borrar previo.
        e.preventDefault();
        if (index > 0) {
          const next = [...chars];
          next[index - 1] = "";
          commit(next);
          focusAt(index - 1);
        }
        return;
      }

      if (key === "ArrowLeft") {
        e.preventDefault();
        focusAt(index - 1);
        return;
      }
      if (key === "ArrowRight") {
        e.preventDefault();
        focusAt(index + 1);
        return;
      }
      if (key === "Home") {
        e.preventDefault();
        focusAt(0);
        return;
      }
      if (key === "End") {
        e.preventDefault();
        focusAt(length - 1);
        return;
      }

      // Permitir Tab, Enter, modifier keys. Bloquear cualquier otra tecla
      // que no sea dígito (letras del teclado numérico ya pasan DIGIT_RE).
      if (key.length === 1 && !DIGIT_RE.test(key) && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
      }
    },
    [chars, commit, focusAt, length],
  );

  const handlePaste = useCallback(
    (index: number, e: ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "");
      if (!pasted) return;
      e.preventDefault();

      const next = [...chars];
      // Pegar desde el slot actual hacia adelante.
      for (let i = 0; i < pasted.length && index + i < length; i++) {
        next[index + i] = pasted[i]!;
      }
      commit(next);

      // Enfocar el siguiente slot vacío o el último.
      const nextEmpty = next.findIndex((c) => !c);
      focusAt(nextEmpty === -1 ? length - 1 : nextEmpty);
    },
    [chars, commit, focusAt, length],
  );

  return (
    <div
      ref={groupRef}
      role="group"
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      className={cn("flex items-center gap-2", className)}
    >
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => {
            inputsRef.current[i] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          maxLength={1}
          value={char}
          disabled={disabled}
          aria-label={`Dígito ${i + 1} de ${length}`}
          aria-invalid={invalid || undefined}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          className={cn(
            "h-14 w-full max-w-[3.25rem] rounded-xl border bg-background text-center",
            "font-mono text-2xl font-medium tabular-nums tracking-tight text-foreground",
            "transition-all duration-150 ease-out",
            "focus-visible:border-ring focus-visible:shadow-ring-focus focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
            char ? "border-foreground/20 bg-accent/20" : "border-input",
            invalid && "border-destructive/60 bg-destructive/[0.04]",
          )}
        />
      ))}
    </div>
  );
});
